import styled from '@emotion/styled';
import React, { type FC, useState } from 'react';
import { HexColorPicker, HexColorInput } from 'react-colorful';

import type { LensDivProps } from '../../types';
import { u, getRadius } from '../../utilities';

const ColorfulWrapper = styled.div`
  padding: var(--lns-space-medium);
  & .react-colorful {
    width: auto;
    height: auto;
  }
  & .react-colorful__saturation {
    height: ${u(14)};
    border-bottom: none;
    box-shadow: inset 0 0 0 1px var(--lns-color-border);
    ${getRadius(100)};
    margin-bottom: var(--lns-space-small);
  }

  & .react-colorful__hue {
    height: ${u(2)};
    width: 100%;
    box-shadow: inset 0 0 0 1px var(--lns-color-border);
    ${getRadius('50')};
    margin-bottom: var(--lns-space-medium);
  }

  & .react-colorful__saturation-pointer {
    width: ${u(1)};
    height: ${u(1)};
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 0 0 3px var(--lns-color-border);
    border-radius: var(--lns-radius-medium);
  }
  & .react-colorful__hue-pointer {
    width: ${u(1)};
    height: ${u(2.5)};
    border-radius: 2px;
    box-shadow: 0 0 0 2px var(--lns-color-border);
    cursor: pointer;
    border: 2px solid white;
  }
`;

const ColorPickerContainer = styled.div`
  position: relative;
  width: ${u(31)};
  background-color: var(--lns-color-overlay);
  ${getRadius('250')};
  box-shadow:
    0 0 0 1px var(--lns-color-border),
    var(--lns-shadow-medium);
`;

const InputContainer = styled.div`
  position: relative;
  width: 100%;

  input {
    padding: 0 0 0 ${u(4)};
    height: ${u(4)};
    width: 100%;
    font: inherit;
    font-size: var(--lns-fontSize-small);
    border: none;
    box-shadow: inset 0 0 0 var(--lns-formFieldBorderWidth)
      var(--lns-color-formFieldBorder);
    ${getRadius('150')};
    transition: 0.3s box-shadow;
    background-color: var(--lns-color-overlay);
    color: var(--lns-color-body);

    &:hover {
      box-shadow: inset 0 0 0 var(--lns-formFieldBorderWidthFocus)
        var(--lns-color-blurple);
    }

    &:focus {
      outline: 1px solid transparent;
      box-shadow: var(--lns-formFieldBorderShadowFocus);
    }
  }
`;

const ColorBox = styled.div<{ color: string }>`
  position: absolute;
  width: ${u(3)};
  height: ${u(3)};
  left: var(--lns-space-xsmall);
  top: var(--lns-space-xsmall);
  border: 1px solid rgba(0, 0, 0, 0.1);
  ${getRadius('100')};
  background-color: ${props => props.color};
`;

const ButtonWrapper = styled.div`
  position: relative;
  border-radius: var(--lns-radius-medium);
  padding: 0 var(--lns-space-medium) var(--lns-space-medium)
    var(--lns-space-medium);
`;

const SwatchesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(7, ${u(3)});
  gap: ${u(1)} ${u(1)};
  border-bottom: 1px solid var(--lns-color-border);
  padding: var(--lns-space-medium);
`;

const Swatch = styled.div<SwatchProps>`
  cursor: pointer;
  width: ${u(3)};
  height: ${u(3)};
  ${getRadius('100')};
  background-color: ${props => props.color};
  border: ${props =>
    props.selected === props.color
      ? '1px solid white'
      : '1px solid var(--lns-color-border)'};
  box-shadow: ${props =>
    props.selected === props.color && '0 0 0 2px var(--lns-color-focusRing)'};
`;

const SwatchSelector: FC<{
  swatches: string[];
  currentColor: string;
  onSwatchClick: (swatch: string) => void;
}> = ({ swatches, currentColor, onSwatchClick }) => {
  const selectedSwatch = swatches.includes(currentColor) && currentColor;

  return (
    <SwatchesContainer>
      {swatches.map(swatch => (
        <Swatch
          key={swatch}
          color={swatch}
          selected={selectedSwatch}
          onClick={() => onSwatchClick(swatch)}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSwatchClick(swatch);
            }
          }}
        />
      ))}
    </SwatchesContainer>
  );
};

const ColorSelector = ({ color, setColor }) => {
  return (
    <ColorfulWrapper>
      <HexColorPicker color={color} onChange={setColor} />
      <InputContainer>
        <HexColorInput prefixed color={color} onChange={setColor} />
        <ColorBox color={color} />
      </InputContainer>
    </ColorfulWrapper>
  );
};

const ColorPicker = ({
  defaultColor = '#ffffff',
  confirmButton,
  swatches,
  onChange,
  ...props
}: ColorPickerProps &
  Omit<
    React.ComponentProps<typeof ColorPickerContainer>,
    keyof ColorPickerProps
  > &
  Omit<
    LensDivProps,
    keyof ColorPickerProps &
      Omit<
        React.ComponentProps<typeof ColorPickerContainer>,
        keyof ColorPickerProps
      >
  >) => {
  const [tempColor, setTempColor] = useState(
    defaultColor ? defaultColor : '#FFFFFF'
  );

  const handleOnChange = (hex: string) => {
    setTempColor(hex);
    onChange(hex);
  };

  const onSwatchClick = (swatch: string) => {
    handleOnChange(swatch);
  };

  return (
    <ColorPickerContainer {...props}>
      {swatches && (
        <SwatchSelector
          swatches={swatches}
          currentColor={tempColor}
          onSwatchClick={onSwatchClick}
        />
      )}
      <ColorSelector color={tempColor} setColor={handleOnChange} />
      {confirmButton && <ButtonWrapper>{confirmButton}</ButtonWrapper>}
    </ColorPickerContainer>
  );
};

type SwatchProps = {
  color?: string;
  selected?: string;
};

type ColorPickerProps = {
  defaultColor?: string;
  confirmButton?: React.ReactNode;
  swatches?: string[];
  onChange?: (selectedColor: string) => void;
};

export default ColorPicker;
